import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";
import { submitContactLead } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Name, phone and project description are required.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitContactLead({
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        address: form.address || undefined,
        message: form.message,
      });
      toast.success(res.message || "Thanks! Your request has been received.");
      setForm({ name: "", phone: "", email: "", address: "", message: "" });
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail || "Couldn't submit. Please call 716-513-4765.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-white border-t border-brand-line"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="eyebrow">Book an estimate</p>
          <h2 className="section-title mt-3">
            Tell us what you need done.
          </h2>
          <p className="mt-6 text-base md:text-lg text-brand-sub leading-relaxed max-w-md">
            Send a quick description of the project. The more detail you can
            share — photos, measurements, address — the faster we can get back
            to you with an honest estimate.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href="tel:17165134765"
              data-testid="contact-info-phone"
              className="flex items-start gap-4 group"
            >
              <span className="mt-1 h-9 w-9 flex-shrink-0 border border-brand-line flex items-center justify-center group-hover:bg-brand-rust group-hover:border-brand-rust transition-colors">
                <Phone className="h-4 w-4 text-brand-ink group-hover:text-white" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-sub">
                  Phone
                </p>
                <p className="font-display text-xl font-bold text-brand-ink group-hover:text-brand-rust transition-colors">
                  716-513-4765
                </p>
              </div>
            </a>
            <a
              href="mailto:artisancontractingny@gmail.com"
              data-testid="contact-info-email"
              className="flex items-start gap-4 group"
            >
              <span className="mt-1 h-9 w-9 flex-shrink-0 border border-brand-line flex items-center justify-center group-hover:bg-brand-rust group-hover:border-brand-rust transition-colors">
                <Mail className="h-4 w-4 text-brand-ink group-hover:text-white" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-sub">
                  Email
                </p>
                <p className="font-display text-base md:text-lg font-bold text-brand-ink group-hover:text-brand-rust transition-colors break-all">
                  artisancontractingny@gmail.com
                </p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="mt-1 h-9 w-9 flex-shrink-0 border border-brand-line flex items-center justify-center">
                <MapPin className="h-4 w-4 text-brand-ink" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-sub">
                  Service Area
                </p>
                <p className="font-display text-base md:text-lg font-bold text-brand-ink">
                  Niagara Falls · Buffalo · Lockport · WNY
                </p>
              </div>
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={onSubmit}
          noValidate
          className="lg:col-span-7 bg-brand-bone border border-brand-line p-7 md:p-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              data-testid="contact-name-input"
              className="field"
              type="text"
              placeholder="Full name"
              value={form.name}
              onChange={update("name")}
              required
            />
            <input
              data-testid="contact-phone-input"
              className="field"
              type="tel"
              placeholder="Phone number"
              value={form.phone}
              onChange={update("phone")}
              required
            />
          </div>
          <input
            data-testid="contact-email-input"
            className="field mt-4"
            type="email"
            placeholder="Email address (optional)"
            value={form.email}
            onChange={update("email")}
          />
          <input
            data-testid="contact-address-input"
            className="field mt-4"
            type="text"
            placeholder="Project city / address"
            value={form.address}
            onChange={update("address")}
          />
          <textarea
            data-testid="contact-message-input"
            className="field mt-4 resize-y"
            rows={6}
            placeholder="Describe the project: painting, flooring, tile, drywall, doors, windows, etc."
            value={form.message}
            onChange={update("message")}
            required
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={loading}
            className="btn-accent mt-5 w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Sending..." : "Request My Free Estimate"}
          </button>
          <p className="mt-4 text-xs text-brand-sub">
            By submitting, you agree to be contacted by Artisan Contracting
            about your project. No marketing spam.
          </p>
        </form>
      </div>
    </section>
  );
}
